import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../lib/auth';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray as Uint8Array;
}

async function apiPost(endpoint: string, body: unknown): Promise<Response> {
  const accessToken = authService.getAccessToken();
  return fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

/**
 * Subscribes the current browser to web push notifications.
 * Call once after login — the hook auto-subscribes when the user is authenticated.
 * On logout, call `unsubscribeWebPush()` to remove the subscription from the backend.
 */
export function useWebPush() {
  const { isAuthenticated } = useAuth();
  const subscriptionRef = useRef<PushSubscription | null>(null);

  const subscribe = useCallback(async () => {
    if (!VAPID_PUBLIC_KEY) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const registration = await navigator.serviceWorker.register('/sw.js');
      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });

      subscriptionRef.current = subscription;

      await apiPost('/api/devices/register/', {
        platform: 'web',
        token: JSON.stringify(subscription),
        device_name: getBrowserName(),
      });
    } catch (err) {
      console.error('[WebPush] subscription failed:', err);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    const sub = subscriptionRef.current;
    if (!sub) return;
    try {
      await apiPost('/api/devices/unregister/', {
        token: JSON.stringify(sub),
      });
      await sub.unsubscribe();
      subscriptionRef.current = null;
    } catch (err) {
      console.error('[WebPush] unsubscribe failed:', err);
    }
  }, []);

  // Auto-subscribe when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      subscribe();
    }
  }, [isAuthenticated, subscribe]);

  return { subscribe, unsubscribe };
}

function getBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Browser';
}
